import React, { useState } from 'react';
import { XIcon, CheckIcon, ChevronRightIcon, ChevronLeftIcon, LayoutTemplateIcon, ClockIcon, WalletIcon } from 'lucide-react';
interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const STEPS = [{
  id: 1,
  title: 'Template Info'
}, {
  id: 2,
  title: 'Channels'
}, {
  id: 3,
  title: 'Budget'
}, {
  id: 4,
  title: 'Review'
}];
export function CreateTemplateModal({
  isOpen,
  onClose
}: CreateTemplateModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Vehicle Launch',
    description: '',
    channels: {
      google: false,
      facebook: false,
      instagram: false,
      whatsapp: false
    },
    budgetRange: '',
    duration: ''
  });
  if (!isOpen) return null;
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const toggleChannel = (channel: keyof typeof formData.channels) => {
    setFormData({
      ...formData,
      channels: {
        ...formData.channels,
        [channel]: !formData.channels[channel]
      }
    });
  };
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} data-id="element-635">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-636">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-637">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-638">
            Create Campaign Template
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500" data-id="element-639">
            <XIcon size={20} data-id="element-640" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-8 py-6" data-id="element-641">
          <div className="flex items-center justify-between relative" data-id="element-642">
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-0.5 bg-gray-200 -z-10" data-id="element-643"></div>
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-0.5 bg-blue-600 -z-10 transition-all duration-300" style={{
            width: `${(currentStep - 1) / (STEPS.length - 1) * 100}%`
          }} data-id="element-644"></div>

            {STEPS.map(step => {
            const isCompleted = step.id < currentStep;
            const isActive = step.id === currentStep;
            return <div key={step.id} className="flex flex-col items-center bg-white px-2" data-id="element-645">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-300 ${isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-646">
                    {isCompleted ? <CheckIcon size={16} data-id="element-647" /> : step.id}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`} data-id="element-648">
                    {step.title}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-4 min-h-[320px]" data-id="element-649">
          {currentStep === 1 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-650">
              <div data-id="element-651">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-652">
                  Template Name
                </label>
                <input type="text" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. New Year Sales Push" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} data-id="element-653" />
              </div>
              <div data-id="element-654">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-655">
                  Category
                </label>
                <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={formData.category} onChange={e => setFormData({
              ...formData,
              category: e.target.value
            })} data-id="element-656">
                  <option data-id="element-657">Vehicle Launch</option>
                  <option data-id="element-658">Seasonal</option>
                  <option data-id="element-659">Service</option>
                  <option data-id="element-660">Exchange</option>
                  <option data-id="element-661">Brand Awareness</option>
                </select>
              </div>
              <div data-id="element-662">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-663">
                  Description
                </label>
                <textarea className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none h-24" placeholder="Describe the template usage and goals..." value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} data-id="element-664" />
              </div>
            </div>}

          {currentStep === 2 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-665">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-666">
                Select Channels
              </label>
              <div className="grid grid-cols-2 gap-4" data-id="element-667">
                {['google', 'facebook', 'instagram', 'whatsapp'].map(channel => <label key={channel} className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${formData.channels[channel as keyof typeof formData.channels] ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-668">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={formData.channels[channel as keyof typeof formData.channels]} onChange={() => toggleChannel(channel as keyof typeof formData.channels)} data-id="element-669" />
                      <span className="ml-3 font-medium text-gray-900 capitalize" data-id="element-670">
                        {channel}
                      </span>
                    </label>)}
              </div>
            </div>}

          {currentStep === 3 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-671">
              <div data-id="element-672">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-673">
                  Recommended Budget Range
                </label>
                <div className="relative" data-id="element-674">
                  <WalletIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-675" />
                  <input type="text" className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. ₹50k - ₹1L" value={formData.budgetRange} onChange={e => setFormData({
                ...formData,
                budgetRange: e.target.value
              })} data-id="element-676" />
                </div>
              </div>
              <div data-id="element-677">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-678">
                  Recommended Duration
                </label>
                <div className="relative" data-id="element-679">
                  <ClockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-680" />
                  <input type="text" className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. 30 days" value={formData.duration} onChange={e => setFormData({
                ...formData,
                duration: e.target.value
              })} data-id="element-681" />
                </div>
              </div>
            </div>}

          {currentStep === 4 && <div className="space-y-6 animate-in slide-in-from-right-4 duration-300" data-id="element-682">
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4" data-id="element-683">
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-684">
                  <span className="text-sm text-gray-500" data-id="element-685">Template Name</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-686">
                    {formData.name || 'Untitled Template'}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-687">
                  <span className="text-sm text-gray-500" data-id="element-688">Category</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-689">
                    {formData.category}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3" data-id="element-690">
                  <span className="text-sm text-gray-500" data-id="element-691">Channels</span>
                  <div className="flex gap-1" data-id="element-692">
                    {Object.entries(formData.channels).filter(([_, enabled]) => enabled).map(([channel]) => <span key={channel} className="px-2 py-0.5 bg-gray-200 rounded text-xs capitalize" data-id="element-693">
                          {channel}
                        </span>)}
                  </div>
                </div>
                <div className="flex justify-between" data-id="element-694">
                  <span className="text-sm text-gray-500" data-id="element-695">Budget Range</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-696">
                    {formData.budgetRange}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-697">
                <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-0.5" data-id="element-698">
                  <CheckIcon size={14} data-id="element-699" />
                </div>
                <div data-id="element-700">
                  <h4 className="text-sm font-medium text-blue-900" data-id="element-701">
                    Ready to Create
                  </h4>
                  <p className="text-xs text-blue-700 mt-1" data-id="element-702">
                    This template will be added to the library and available for
                    all partners to use.
                  </p>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-703">
          <button onClick={handleBack} className={`flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors ${currentStep === 1 ? 'invisible' : ''}`} data-id="element-704">
            <ChevronLeftIcon size={16} className="mr-1" data-id="element-705" />
            Back
          </button>
          <div className="flex gap-3" data-id="element-706">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-707">
              Cancel
            </button>
            <button onClick={handleNext} className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-all transform active:scale-95" data-id="element-708">
              {currentStep === STEPS.length ? 'Create Template' : 'Next Step'}
              {currentStep !== STEPS.length && <ChevronRightIcon size={16} className="ml-1" data-id="element-709" />}
            </button>
          </div>
        </div>
      </div>
    </div>;
}